import React from 'react';
import {
  Box,
  FormControlLabel,
  InputAdornment,
  Link,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import COLORS from 'theme/colors';
import { Locations } from 'utils/dropDownList';
import { CONSULTATION_TYPES } from './types';

export interface SelfSchedulingFilters {
  consultationTypes: string[];
  locations: string[];
}

// Locations.location is the clinic's full street address ("...Maryville, IL") - its state is
// always the last comma-separated segment. Derived from that single hand-maintained list rather
// than a second hardcoded state map, so there's nothing to keep in sync if a clinic is added.
// Display only - the underlying filter value (and everything it's matched against downstream,
// e.g. SelfScheduling/index.tsx's FILTER_TO_BACKEND_CITY) stays the bare city name.
const STATE_BY_FILTER_VALUE: Record<string, string> = Object.fromEntries(
  Locations.map((loc) => [loc.filterValue, loc.location.split(',').pop()?.trim() ?? ''])
);

export const formatLocationLabel = (filterValue: string): string => {
  const state = STATE_BY_FILTER_VALUE[filterValue];
  return state ? `${filterValue}, ${state}` : filterValue;
};

export interface ProviderAvailabilityNavState {
  filters: SelfSchedulingFilters;
  searchText: string;
  // The real office id behind filters.locations[0] (a display name) - resolved once on the
  // list page (see SelfScheduling/index.tsx's handleMore) so the calendar can stay scoped to
  // the same clinic without re-resolving the name-to-id lookup itself.
  officeId?: number;
  // Set when SelfScheduling/index.tsx redirected straight here for an existing patient (one
  // with a primary provider already assigned) instead of the patient arriving via "More" from
  // the directory - there's no directory visit to go back to, and going back to
  // /self-scheduling would just redirect here again, so ProviderAvailability sends this
  // patient's back button to the dashboard instead (see its backDir).
  directEntry?: boolean;
  // Set when ManageAppointment sent the patient here to reschedule an existing appointment
  // rather than to book a new one - selecting a slot then calls reschedule instead of book, and
  // the confirm dialog's wording reflects that. currentSlot carries the appointment's own
  // existing schedule (needed to free its slot) since the reschedule request needs both the old
  // and new slot, not just the new one.
  reschedule?: {
    appointmentId: number;
    // Known from the appointment being managed even before/if this provider's full
    // self-scheduling directory record resolves - lets the fallback provider (see
    // ProviderAvailability's buildFallbackProvider) show the real name instead of a generic
    // placeholder.
    providerName?: string;
    currentSlot: {
      scheduledTime: string; // yyyy-MM-dd'T'HH:mm:ss, office-local, no offset (see AppointmentVO)
      examRoom: number;
      duration: number;
      profileId: number;
      officeId: number;
      patientId: number;
      // The appointment's own current provider - needed to populate the reschedule request's
      // `current` slot (see selfSchedulingServices.rescheduleSelfSchedulingAppointmentAPI's
      // nested current/replacement payload), not just the newly selected `replacement` one.
      providerId: number;
    };
    // Ready-to-render summary of the appointment being replaced - shown alongside the newly
    // selected slot in the confirm dialog and, on the directory (SelfScheduling/index.tsx), as a
    // banner above the provider list. Computed once by ManageAppointment (which already has this
    // data formatted for its own page) instead of re-deriving day/time from currentSlot's raw
    // fields on every page a reschedule can land on.
    currentSummary: {
      day: string;
      time: string;
      officeAddress?: string;
    };
  };
}

// Returns a node (not a plain string) so each filter's value can render bold while its label
// stays regular weight - both current callers already render this directly inside a Typography.
export const describeFilters = (filters: SelfSchedulingFilters): React.ReactNode => {
  const hasConsultationTypes = filters.consultationTypes.length > 0;
  const hasLocations = filters.locations.length > 0;
  if (!hasConsultationTypes && !hasLocations) {
    return null;
  }
  return (
    <>
      {hasConsultationTypes ? (
        <>
          Consultation Type: <b>{filters.consultationTypes.join(', ')}</b>
        </>
      ) : null}
      {hasConsultationTypes && hasLocations ? '.  ' : null}
      {hasLocations ? (
        <>
          Location: <b>{filters.locations.map(formatLocationLabel).join(', ')}</b>
        </>
      ) : null}
    </>
  );
};

interface FiltersPanelProps {
  filters: SelfSchedulingFilters;
  locations: string[];
  locationSearch: string;
  onLocationSearchChange: (value: string) => void;
  // Applies immediately - picking a radio option fetches fresh providers right away (see
  // SelfScheduling/index.tsx's handleToggleFilter), no separate Apply step.
  onToggle: (category: keyof SelfSchedulingFilters, value: string) => void;
  onClearFilters: () => void;
  // Changes whenever a selection was refused (e.g. In-person at a Missouri clinic for an
  // Illinois-preferred patient - see SelfScheduling/index.tsx's handleToggleFilter). Used as the
  // radio groups' key, so they re-mount and show the unchanged, previous selection again: a refused
  // click leaves the browser's radio checked, and since filters didn't change nothing else would
  // uncheck it.
  selectionResetKey?: number;
}

const FiltersPanel = ({
  filters,
  locations,
  locationSearch,
  onLocationSearchChange,
  onToggle,
  onClearFilters,
  selectionResetKey = 0,
}: FiltersPanelProps) => {
  const filteredLocations = locations.filter((location) =>
    location.toLowerCase().includes(locationSearch.trim().toLowerCase())
  );

  return (
    <Box
      sx={{
        border: `1px solid ${COLORS.Gray300}`,
        borderRadius: '12px',
        backgroundColor: COLORS.White,
        p: 3,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h3' sx={{ color: COLORS.Gray900 }}>
          Filters
        </Typography>
        <Link
          component='button'
          type='button'
          underline='hover'
          onClick={onClearFilters}
          sx={{ color: COLORS.Link, fontSize: 14 }}
        >
          Clear filters
        </Link>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant='h4' sx={{ mb: 1, fontWeight: 600, color: COLORS.Gray900 }}>
          Consultation Type
        </Typography>
        {/* Only one consultation type is ever active - matches Location below (one clinic at a
            time) rather than the old checkbox multi-select. */}
        <RadioGroup
          key={`consultationTypes-${selectionResetKey}`}
          value={filters.consultationTypes[0] ?? ''}
          onChange={(e) => onToggle('consultationTypes', e.target.value)}
        >
          {CONSULTATION_TYPES.map((option) => (
            <FormControlLabel
              key={option}
              value={option}
              control={
                <Radio
                  size='small'
                  sx={{
                    color: COLORS.Gray300,
                    '&.Mui-checked': { color: COLORS.DarkBlue },
                  }}
                />
              }
              label={<Typography variant='body2' sx={{ color: COLORS.Text }}>{option}</Typography>}
            />
          ))}
        </RadioGroup>
      </Box>

      <Box>
        <Typography variant='h4' sx={{ mb: 1, fontWeight: 600, color: COLORS.Gray900 }}>
          Location
        </Typography>
        <TextField
          size='small'
          fullWidth
          placeholder='Search location'
          value={locationSearch}
          onChange={(e) => onLocationSearchChange(e.target.value)}
          sx={{ mb: 1, backgroundColor: COLORS.White, borderRadius: '8px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{ color: COLORS.Gray600, fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        {/* Only one location is ever active - switching locations re-fetches free slots
            scoped to just that clinic, so this is a radio choice, not a multi-select. */}
        <RadioGroup
          key={`locations-${selectionResetKey}`}
          value={filters.locations[0] ?? ''}
          onChange={(e) => onToggle('locations', e.target.value)}
        >
          {filteredLocations.map((location) => (
            <FormControlLabel
              key={location}
              value={location}
              control={
                <Radio
                  size='small'
                  sx={{
                    color: COLORS.Gray300,
                    '&.Mui-checked': { color: COLORS.DarkBlue },
                  }}
                />
              }
              label={
                <Typography variant='body2' sx={{ color: COLORS.Text }}>
                  {formatLocationLabel(location)}
                </Typography>
              }
            />
          ))}
        </RadioGroup>
        {filteredLocations.length === 0 ? (
          <Typography variant='body2' sx={{ color: COLORS.Gray600, mt: 1 }}>
            No matching locations
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default FiltersPanel;
